import React, { useState, useEffect, ReactNode, useRef } from 'react';
import {
  useGetPSUElectionWindowConfiguration,
  useGetSettlementType,
  useWealthlaneAwardApi,
  useWealthlaneAwardSettlementApi,
} from '../../../../../hooks';
import { ElectionCardBlock, ElectionWindowWrapper } from './electionWindow.styled';
import Heading from '../../../../../components/typography';
import ClickableTooltip from '../../../../../components/tooltip/clickTooltip';
import { MaterialIconFilled, checkNaNValue } from '../../../../../utils/global';
import { Form, Formik } from 'formik';
import InputText from '../../../../../components/input-text/inputText';
import Button from '../../../../../components/button';
import ElectionCustomDropdown from '../../../../../components/custom-dropdown/electionDropdown';
import { PsuElectionWindowConfigurationService } from '@wealthlane/api-services/wealthlane-award-settlement-service';
import Tag from '../../../../../components/lozenge/tags';
import { DropdownType } from '../../../../../types/common';
import { useMutation, useQueryClient } from 'react-query';
import { ELECTION_CONFIGURATION } from '../../../../../utils/validations/validations';
import { toast } from 'react-toastify';
import ToastComponent from '../../../../../components/toast';
import _, { isEmpty } from 'lodash';
import Spinners from '../../../../../components/spinner';
import FormSelect from '../../../../../components/dropdown/formSelect';

interface PsuElectionType {
  refresher: boolean;
}

const PsuElectionConfig = ({ refresher }: PsuElectionType) => {
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneAwardSettlementApi();
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const { data: psuConfig, isLoading: psuLoader } =
    useGetPSUElectionWindowConfiguration(handleRequest);
  const [defaultPsuValue, setDefaultPsuValue] = useState<string>('');
  const [viewSelected, setViewSelected] = useState<boolean>(false);
  const [apiData, setApiData] = useState<string[]>([]);
  const { data: settlementTypeOption, isLoading: loadingSettlementTypeOption } =
    useGetSettlementType(awardHandleRequest);

  const [initialData, setInitialData] = useState<any>(psuConfig);

  useEffect(() => {
    if (psuConfig !== null) {
      setInitialData(psuConfig);
    }
  }, [psuConfig]);

  useEffect(() => {
    setApiData(
      psuConfig != null
        ? psuConfig?.settlementTypeIds?.map((item: any) => item?.settlementTypeId)
        : []
    );
  }, [psuConfig]);

  useEffect(() => {
    apiData !== undefined && apiData?.length > 0 && setViewSelected(true);
    apiData !== undefined ? setCheckedList([...apiData]) : setCheckedList(apiData);
  }, [apiData]);

  const handleCheck = (value: string, setField: any, data?: string): void => {
    setField('settlementValidity', isEmpty(checkedList) ? true : viewSelected);
    setViewSelected(true);
    const keys =
      checkedList && checkedList?.length > 0
        ? checkedList?.includes(value)
          ? checkedList.filter((item) => item !== value)
          : [...checkedList, value]
        : [value];

    data && checkedList && !keys.includes(data) && setField('defaultSettlementTypeId', '');

    setCheckedList(keys);
  };

  const transformObjectToNull = (values: any): any => {
    const obj: any = {};
    Object.entries(values)?.forEach(([key, value]) => {
      // We have to convert the values of object which contains boolean value only
      obj[key] = typeof value === 'number' && value === 0 ? null : value;
    });
    obj.settlementValidity = 'invalid';
    return obj;
  };

  const usePostPsuElectionConfig = (): any => {
    return useMutation(
      async (payload: any) => {
        return await handleRequest(
          PsuElectionWindowConfigurationService.postApiAwardSettlementApiAppPSUElectionWindowConfigurationSavePSuElectionWindowConfigurationV2(
            payload
          )
        );
      },
      {
        onSuccess: (response: any) => {
          if (response != null) {
            toast.success(<ToastComponent label='Success' message={'Configuration saved'} />);
            void queryClient.invalidateQueries('getPSUElectionWindowConfigurationList');
          }
        },
        onError: (err) => {
          console.log('error', err);
        },
      }
    );
  };

  const { mutate: mutatePsu, isLoading } = usePostPsuElectionConfig();

  const handleSubmitPsu = (values: any): void => {
    delete values.settlementValidity;
    const payload = {
      ...values,
      defaultSettlementConfigurationDto: {
        ...values?.defaultSettlementConfigurationDto,
        defaultSettlementTypeId: values.defaultSettlementTypeId,
      },
      planTypeCategory: psuConfig?.planTypeCategory,
      planTypeCategoryId: psuConfig?.planTypeCategoryId,
      electionWindowConfigurationId: psuConfig?.electionWindowConfigurationId ?? '',
      settlementTypeIds: checkedList
        .filter((id, index, arr) => arr.indexOf(id) === index)
        .map((id) => ({ settlementTypeId: id })),
    };

    if (_.isEqual(payload, psuConfig)) {
      toast.success(<ToastComponent label='Info' message={'No changes made'} />);
    } else {
      mutatePsu(payload);
    }
  };

  const counter = useRef(0);

  return (
    <ElectionWindowWrapper id="psuElectionConfigWrapper">
      <div className='flex gap-x-5 mb-2' id="psuElectionConfigHeader">
        <Heading
          variant='h4'
          title={'Set Up Tax Settlement Method - PSU'}
          className='election-head'
          id="psuElectionConfigTitle"
        />
        <ClickableTooltip
          tooltipText={
            'Set up here if you support different tax settlement options for grant releases and exercises. Select all the methods you want to make available in "Settlement Method" below. Then select the tax settlement election window. If participants do not elect a tax settlement method prior to window closing, they will be set to Company\'s default tax settlement method above. Election windows are from each release or exercise event dates.'
          }
          size='sm'
          variant='dark'>
          <span className='material-symbols-sharp' style={MaterialIconFilled}>
            info
          </span>
        </ClickableTooltip>
      </div>

      <ElectionCardBlock id="psuElectionConfigCardBlock">
        {psuLoader && <Spinners />}
        {!psuLoader && (
          <Formik
            initialValues={initialData != null ? transformObjectToNull(initialData) : {}}
            validationSchema={ELECTION_CONFIGURATION}
            enableReinitialize
            onSubmit={(values) => {
              handleSubmitPsu(values);
            }}>
            {({ touched, errors, setFieldValue, handleChange, values }) => {
              useEffect(() => {
                setFieldValue('settlementValidity', isEmpty(checkedList) ? true : viewSelected);
              }, [checkedList]);
              return (
                <Form id='psuElectionConfigForm'>
                  <div className='grid lg:grid-cols-12 md:grid-cols-1 gap-x-8 ' id="psuElectionConfigFormContainer">
                    <div className='lg:col-span-2 col-span-1 ' id="electionWindowStartsContainer">
                      <InputText
                        type='days'
                        label='Election Window Starts'
                        name='startFromVestingDateInDays'
                        id='startFromVestingDateInDays'
                        onChange={(e: any) => {
                          checkNaNValue(
                            e?.target?.value,
                            'startFromVestingDateInDays',
                            setFieldValue
                          );
                        }}
                        errors={errors}
                        touched={touched}
                        required
                      />
                    </div>
                    <div className='lg:col-span-2 col-span-1 ' id="electionWindowEndsContainer">
                      <InputText
                        type='days'
                        label='Election Window Ends'
                        name='endFromVestingDateInDays'
                        id='endFromVestingDateInDays'
                        onChange={(e: any) => {
                          checkNaNValue(
                            e?.target?.value,
                            'endFromVestingDateInDays',
                            setFieldValue
                          );
                        }}
                        errors={errors}
                        touched={touched}
                        required
                      />
                    </div>
                    <div className='lg:col-span-4 col-span-1 mb-6' id="settlementMethodsContainer">
                      <ElectionCustomDropdown
                        id='settlementMethod'
                        name='settlementMethod'
                        title='Tax Settlement Methods Available'
                        display={viewSelected}
                        handleUser={(value: string) => {
                          counter.current += 1;
                          handleCheck(value, setFieldValue, values.defaultSettlementTypeId);
                        }}
                        data={settlementTypeOption}
                        checkedList={checkedList}
                        errorMessage={errors?.settlementMethod as string}
                        toggleItems={{
                          displaySelected: true,
                        }}
                      />
                    </div>
                    <FormSelect
                      key={`psu-default-tax-settlement-method-${counter.current}`}
                      type='text'
                      label={'Default Tax Settlement Method'}
                      name={`defaultSettlementTypeId`}
                      id='defaultSettlementTypeId'
                      onChange={(value: string) => {
                        handleChange(value);
                        checkedList && checkedList?.length > 0
                          ? !checkedList?.includes(value)
                            ? handleCheck(value, setFieldValue)
                            : null
                          : handleCheck(value, setFieldValue);
                      }}
                      errors={errors}
                      touched={touched}
                      options={settlementTypeOption}
                      isLoading={loadingSettlementTypeOption}
                      values={values}
                      arrValue={values.defaultSettlementTypeId}
                      setFieldValue={setFieldValue}
                      containerClassName='formGroup'
                      width='200px'
                      required
                    />
                  </div>
                  <div id="savePsuElectionConfigButtonContainer">
                    <Button
                      // btnClassName='mt-[24px]'
                      title={`Save`}
                      type='submit'
                      variant={'primary'}
                      loading={isLoading}
                      id="savePsuElectionConfigButton"
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </ElectionCardBlock>
    </ElectionWindowWrapper>
  );
};

export default PsuElectionConfig;
